import ConfirmationModal from "components/ConfirmationModal";
import DocumentViewerModal from "components/DocumentViewer";
import FileActions from "components/FileDownloadButtons";
import { StatusChip } from "components/StatusChip";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { editAccusedSummon } from "services/prosecutorDecipilineService";
import { AccusedSummon, Complaints } from "types";
import { ACCUSED_COMPLAINT_STATUS } from "utils/constantValues";
import { DateView } from "utils/helper";
import { ROLES } from "utils/roles";
import AccusedSummonConfirmationModal from "./AccusedSummonConfirmationModal";
import AddSummonFeedbackModal from "./AddSummonFeedbackModal";
import AddSummonRegistrationModal from "./AddSummonRegistrationModal";
import AddSummonResponse from "./AddSummonResponse";
import { useTranslation } from "contexts/i18nContext";

interface SummonProps {
  summonData: AccusedSummon;
  compliantData: Complaints;
  onSummonRefresh: any;
  onComplaintsRefresh: any;
}

export default function AccusedSummonCard({
  summonData,
  compliantData,
  onSummonRefresh,
  onComplaintsRefresh,
}: SummonProps) {
  const { t } = useTranslation();

  const canCoordinatorConfirmSummon = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  const canAccusedConfirmSummon = useHasRole([ROLES.INSPECTION_PROSECUTOR]);
  const canUpdateHearingDate = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canCreateAccusedFeedback = useHasRole([
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_COORDINATOR,
  ]);
  const canAddAccusedRegistration = useHasRole([ROLES.INSPECTION_PROSECUTOR]);

  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState<boolean>(false);
  const [coordinatorConfirmationModal, setCoordinatorConfirmationModal] =
    useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [isEditingHearingDate, setIsEditingHearingDate] = useState(false);
  const [hearingDateDraft, setHearingDateDraft] = useState("");
  const [isUpdatingHearingDate, setIsUpdatingHearingDate] = useState(false);

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const startHearingDateEdit = () => {
    setHearingDateDraft(toDateInputValue(summonData?.hearing_date));
    setIsEditingHearingDate(true);
  };

  const cancelHearingDateEdit = () => {
    setIsEditingHearingDate(false);
    setHearingDateDraft("");
  };

  const handleUpdateHearingDate = async () => {
    if (!summonData?.id) {
      showToast(t("summonNotFound"), "error");
      return;
    }

    const currentDate = toDateInputValue(summonData?.hearing_date);
    if (!hearingDateDraft || hearingDateDraft === currentDate) {
      showToast("No date change detected", "error");
      return;
    }

    setIsUpdatingHearingDate(true);
    try {
      await editAccusedSummon(
        {
          hearing_date: hearingDateDraft,
        } as any,
        summonData.id,
        false,
      );

      await Promise.resolve(onSummonRefresh?.());
      cancelHearingDateEdit();
      showToast("Hearing date updated", "success");
    } catch (err) {
      showToast("Failed to update hearing date", "error");
    } finally {
      setIsUpdatingHearingDate(false);
    }
  };

  const handleApproveClick = async () => {
    if (!summonData) {
      showToast(t("summonNotFound"), "error");
      return;
    }
    setLoading(true);

    const isAccusedConfirmed = true;
    const isCoordinatorConfirmed = true;

    const data = new FormData();
    data.append("accused_confirmed", isAccusedConfirmed.toString());
    data.append("cordinator_confirmed", isCoordinatorConfirmed.toString());
    data.append("status", ACCUSED_COMPLAINT_STATUS.APPROVED);

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editAccusedSummon(data, summon_id, isFormData);

      onSummonRefresh();
      setShowConfirmationModal(false);
      showToast(t("successfullyConfirmed"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!summonData) return null;

  return (
    <div className="mb-8">
      {summonData && (
        <div
          className={
            "mx-auto bg-white border-black border-8 rounded-xl  overflow-hidden mb-4"
          }
        >
          <div className="p-5">
            <h1 className="text-1xl font-bold text-gray-800 mb-3">
              {t("accusedSummon")}
            </h1>

            {summonData?.assigned_prosecutor && (
              <div className="relative border border-gray-400 bg-white rounded-md p-2 my-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img
                      src={require("../../../images/avatar.png")}
                      alt="avatar"
                      className="w-8 h-8 rounded-full"
                    />
                    <div>
                      <p className="font-medium text-sm">
                        {summonData?.assigned_prosecutor || "-"}
                      </p>
                      <p className="text-xs text-gray-500">{t("assignedTo")}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="">
              <hr />

              <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                <p className="block text-gray-600">{t("hearingDate")}</p>
                <div className="flex items-center gap-3">
                  <p className="block text-black text-sm font-medium">
                    {summonData?.hearing_date
                      ? DateView(summonData.hearing_date)
                      : "-"}
                  </p>
                  {canUpdateHearingDate && (
                    <button
                      type="button"
                      onClick={startHearingDateEdit}
                      className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                    >
                      {t("updateDate")}
                    </button>
                  )}
                </div>
              </div>

              {isEditingHearingDate && (
                <div className="my-3 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t("hearingDate")}
                    </label>
                    <input
                      type="date"
                      value={hearingDateDraft}
                      onChange={(e) => setHearingDateDraft(e.target.value)}
                      className="w-full md:w-64 rounded border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={isUpdatingHearingDate}
                      onClick={handleUpdateHearingDate}
                      className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                    >
                      {isUpdatingHearingDate ? "Updating..." : "Save Date"}
                    </button>
                    <button
                      type="button"
                      disabled={isUpdatingHearingDate}
                      onClick={cancelHearingDateEdit}
                      className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                    >
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              )}

              <hr />

              <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                <p className="block text-gray-600">{t("addressInformation")}</p>
                <p className="block text-black text-sm font-medium">
                  {`R-${summonData?.region || ""},  Z-${
                    summonData?.zone || ""
                  },  W-${summonData?.woreda || ""},  K-${
                    summonData?.kebele || ""
                  }`}
                </p>
              </div>

              <hr />

              <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                <p className="block text-gray-600">{t("registrationNumber")}</p>
                <p className="block text-black text-sm font-medium">
                  {summonData?.registration_number}
                </p>
              </div>

              <hr />

              <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                <p className="block text-gray-600">{t("registrationDate")}</p>
                <p className="block text-black text-sm font-medium">
                  {summonData?.registration_date}
                </p>
              </div>

              <hr />
            </div>

            {/* Summary Desc */}
            <div className="my-4">
              <h1 className="text-1xl font-bold text-gray-800 mb-1">
                {t("claimSummary")}
              </h1>
              <p className="text-gray-600 leading-relaxed">
                {summonData?.claim_summary || "-"}
              </p>
            </div>

            {/* Files */}

            {summonData?.claim_summary_file_details && (
              <div className="flex justify-between items-center p-2 bg-gray-50 rounded-md my-3">
                <div className="flex items-center gap-2">
                  <div className="bg-[#5e3b1f] text-white font-bold text-xs px-2 py-1 rounded">
                    PDF
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {summonData?.claim_summary_file_details?.file_name ||
                        t("claimSummaryDoc")}
                    </p>
                    {/* <p className="text-xs text-gray-500">11.3MB</p> */}
                  </div>
                </div>

                <FileActions
                  fileUrl={summonData?.claim_summary_file_details?.url || ""}
                />
              </div>
            )}

            <DocumentViewerModal
              documents={
                (summonData?.uploaded_documents?.filter(
                  (a: any) => a?.document_type !== "response",
                ) || []) as any
              }
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/litigation-counciloffice-documents/document`}
              layout="list"
              label={t("letterUpload")}
              hideEmptyFile={true}
            />

            {/* Response Summary Desc */}
            {summonData?.response_summary && (
              <>
                <hr />
                <div className="my-4">
                  <h1 className="text-1xl font-bold text-gray-800 mb-1">
                    {t("responseSummary")}
                  </h1>
                  <p className="text-gray-600 leading-relaxed">
                    {summonData?.response_summary || "-"}
                  </p>
                </div>
                <DocumentViewerModal
                  documents={
                    (summonData?.uploaded_documents?.filter(
                      (a: any) => a?.document_type === "response",
                    ) || []) as any
                  }
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/litigation-counciloffice-documents/document`}
                  layout="list"
                  label={t("letterUpload")}
                  hideEmptyFile={true}
                />
              </>
            )}

            {summonData?.coordinator_feedbacks?.length > 0 && (
              <div className="bg-gray-700 p-5 rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-white">{t("feedbackType")}</p>
                  <StatusChip
                    status={
                      summonData?.status ===
                        ACCUSED_COMPLAINT_STATUS.REVISION ||
                      summonData?.status ===
                        ACCUSED_COMPLAINT_STATUS.FORWARDED_TO_COORDINATOR
                        ? "Reject"
                        : "Approved"
                    }
                  />
                </div>
                <p className="text-sm text-gray-100 leading-relaxed">
                  {summonData?.coordinator_feedbacks?.sort(
                    (a: any, b: any) =>
                      new Date(b?.created_at ?? "").getTime() -
                      new Date(a?.created_at ?? "").getTime(),
                  )[0]?.comment || ""}
                </p>
              </div>
            )}
          </div>

          {canCreateAccusedFeedback &&
            summonData?.response_summary &&
            summonData?.status ===
              ACCUSED_COMPLAINT_STATUS.FORWARDED_TO_COORDINATOR && (
              <button
                className="w-full bg-[#5f5f5f] hover:bg-gray-800 border border-white text-white my-3 py-2 rounded-lg "
                onClick={() => setIsFeedbackOpen(true)}
              >
                {t("addFeedback")}
              </button>
            )}
        </div>
      )}

      {canCoordinatorConfirmSummon &&
        !summonData?.cordinator_confirmed &&
        summonData?.status ===
          ACCUSED_COMPLAINT_STATUS.FORWARDED_TO_COORDINATOR && (
          <div className="text-right">
            <button
              className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
              type="submit"
              onClick={() => setCoordinatorConfirmationModal(true)}
            >
              {t("assignProsecutor")}
            </button>
          </div>
        )}

      {canAccusedConfirmSummon &&
        (summonData?.status === ACCUSED_COMPLAINT_STATUS.REVISION ||
          summonData?.status ===
            ACCUSED_COMPLAINT_STATUS.ASSIGNED_TO_PROSECUTOR) &&
        summonData?.cordinator_confirmed && (
          <div className="text-right">
            <button
              className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
              type="submit"
              onClick={() => setShowResponseModal(true)}
            >
              {t("addResponse")}
            </button>
          </div>
        )}

      {canAddAccusedRegistration &&
        summonData?.status === ACCUSED_COMPLAINT_STATUS.APPROVED &&
        !summonData?.registration_number && (
          <div className="text-right">
            <button
              className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
              type="submit"
              onClick={() => setShowRegistrationModal(true)}
            >
              {t("addRegistrationNo")}
            </button>
          </div>
        )}

      {summonData && compliantData && (
        <AccusedSummonConfirmationModal
          isOpen={coordinatorConfirmationModal}
          setIsOpen={setCoordinatorConfirmationModal}
          complaintData={compliantData}
          summonData={summonData}
          onRefresh={onSummonRefresh}
          onComplaintsRefresh={onComplaintsRefresh}
        />
      )}

      {summonData && compliantData && (
        <AddSummonResponse
          isOpen={showResponseModal}
          setIsOpen={setShowResponseModal}
          summonData={summonData}
          complaintData={compliantData}
          onRefresh={onSummonRefresh}
        />
      )}

      {summonData && compliantData && (
        <AddSummonFeedbackModal
          complaintData={compliantData}
          isOpen={isFeedbackOpen}
          setIsOpen={setIsFeedbackOpen}
          summonData={summonData}
          onRefresh={onSummonRefresh}
        />
      )}

      {summonData && compliantData && (
        <AddSummonRegistrationModal
          isOpen={showRegistrationModal}
          setIsOpen={setShowRegistrationModal}
          summonData={summonData}
          complaintData={compliantData}
          onRefresh={onSummonRefresh}
        />
      )}

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleApproveClick}
        isLoading={loading}
      />
    </div>
  );
}
